import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Check, ChevronUp, ChevronDown, Image, Type, Sparkles } from 'lucide-react';
import { api } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import useNotificationFeedStore from '../store/useNotificationFeedStore';
import { useLocation } from 'react-router-dom';

const typeIcons = {
  Typo: Type,
  Product: Image,
  ProductTypo: Image,
  ProductAd: Image,
  AIGen: Sparkles,
  AIGenTypo: Sparkles,
  AIGenAd: Sparkles,
};

const DraftPostsBubble = () => {
  const { pathname } = useLocation();
  const { accessKey } = useAppStore();
  const [draftPosts, setDraftPosts] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [completedPosts, setCompletedPosts] = useState([]);
  const prevDraftIdsRef = useRef(new Set());
  const initialFetchDone = useRef(false);

  // The component re-fetches whenever a "post" push notification arrives
  // (added to the feed store by PushNotificationFeedBridge). The polling
  // setInterval is gone — pushes are the source of truth.
  const latestPostNotificationId = useNotificationFeedStore((s) => {
    const latest = s.items.find((item) => item.type === 'post');
    return latest ? latest.id : null;
  });

  const fetchPosts = useCallback(async () => {
    if (!accessKey) return;
    try {
      const posts = await api.checkPostsReady();
      const list = Array.isArray(posts) ? posts : [];
      const drafts = list.filter((p) => p.status === 'draft');
      const currentIds = new Set(drafts.map((p) => p.id));

      // Detect completions (only after initial fetch)
      if (initialFetchDone.current && prevDraftIdsRef.current.size > 0) {
        const newlyCompleted = [];
        for (const id of prevDraftIdsRef.current) {
          if (!currentIds.has(id)) {
            // Find the post info from previous drafts
            const post = draftPosts.find((p) => p.id === id);
            if (post) newlyCompleted.push(post);
          }
        }
        if (newlyCompleted.length > 0) {
          setCompletedPosts((c) => [...c, ...newlyCompleted]);
          setTimeout(() => {
            setCompletedPosts((c) => c.filter((p) => !newlyCompleted.find((nc) => nc.id === p.id)));
          }, 8000);
        }
      }

      prevDraftIdsRef.current = currentIds;
      initialFetchDone.current = true;
      setDraftPosts(drafts);
    } catch {
      // ignore
    }
  }, [accessKey, draftPosts]);

  // Initial fetch on mount.
  useEffect(() => {
    fetchPosts();
  }, []);

  // Re-fetch when a new "post" notification lands (post created/ready,
  // post failed, post published, post regenerated all qualify).
  useEffect(() => {
    if (!latestPostNotificationId) return;
    fetchPosts();
  }, [latestPostNotificationId]);

  // Re-show if dismissed but new drafts appear
  useEffect(() => {
    if (draftPosts.length > 0 && dismissed) {
      setDismissed(false);
      setExpanded(true);
    }
  }, [draftPosts.length]);

  const totalVisible = draftPosts.length + completedPosts.length;
  const pageHasInlineDraftState = pathname === '/dashboard' || pathname.startsWith('/posts/');
  if (pageHasInlineDraftState) return null;
  if (totalVisible === 0 || dismissed) return null;

  return (
    <div
      className='fixed bottom-6 right-6 z-50 rounded-2xl overflow-hidden shadow-2xl'
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        width: '340px',
        maxWidth: 'calc(100vw - 3rem)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Header */}
      <button
        type='button'
        onClick={() => setExpanded(!expanded)}
        className='flex items-center gap-3 px-5 py-4 w-full cursor-pointer'
      >
        <div
          className='w-9 h-9 rounded-xl flex items-center justify-center shrink-0'
          style={{ background: 'var(--brand)', color: 'white' }}
        >
          {draftPosts.length > 0 ? (
            <Loader2 className='animate-spin' style={{ width: '1.125rem', height: '1.125rem' }} />
          ) : (
            <Check style={{ width: '1.125rem', height: '1.125rem' }} />
          )}
        </div>
        <div className='flex-1 text-left'>
          <p className='text-sm font-semibold' style={{ color: 'var(--text-primary)' }}>
            {draftPosts.length > 0 ? 'Generating Posts' : 'Posts Ready'}
          </p>
          <p className='text-xs' style={{ color: 'var(--text-muted)' }}>
            {draftPosts.length > 0
              ? `${draftPosts.length} post${draftPosts.length !== 1 ? 's' : ''} in progress`
              : `${completedPosts.length} post${completedPosts.length !== 1 ? 's' : ''} completed`}
          </p>
        </div>
        {expanded ? (
          <ChevronDown style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
        ) : (
          <ChevronUp style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className='px-4 pb-4'>
          <div
            className='space-y-2 max-h-64 overflow-y-auto thin-scrollbar'
            style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '0.75rem' }}
          >
            {/* Active drafts */}
            {draftPosts.map((post) => {
              const Icon = typeIcons[post.type] || Sparkles;
              return (
                <div
                  key={post.id}
                  className='flex items-center gap-3 px-3 py-2.5 rounded-xl'
                  style={{ background: 'var(--bg-surface)' }}
                >
                  <Loader2
                    className='animate-spin shrink-0'
                    style={{ width: '0.875rem', height: '0.875rem', color: 'var(--brand)' }}
                  />
                  <Icon className='shrink-0' style={{ width: '0.875rem', height: '0.875rem', color: 'var(--text-muted)' }} />
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-medium truncate' style={{ color: 'var(--text-primary)' }}>
                      {post.type || 'Post'}
                    </p>
                    <p className='text-xs truncate' style={{ color: 'var(--text-ghost)' }}>
                      Generating...
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Recently completed */}
            {completedPosts.map((post) => {
              const Icon = typeIcons[post.type] || Sparkles;
              return (
                <div
                  key={post.id}
                  className='flex items-center gap-3 px-3 py-2.5 rounded-xl'
                  style={{ background: 'var(--bg-surface)' }}
                >
                  <Check
                    className='shrink-0'
                    style={{ width: '0.875rem', height: '0.875rem', color: 'var(--brand)' }}
                  />
                  <Icon className='shrink-0' style={{ width: '0.875rem', height: '0.875rem', color: 'var(--brand)' }} />
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-medium' style={{ color: 'var(--brand)' }}>
                      {post.type || 'Post'} — Ready!
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dismiss */}
          <button
            type='button'
            onClick={() => setDismissed(true)}
            className='w-full text-center mt-3 py-1.5 text-xs cursor-pointer rounded-lg transition-colors'
            style={{ color: 'var(--text-ghost)' }}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

export default DraftPostsBubble;
